// Sorting, searching and CSV building for the coordinator tables. A column is
// { key, label, type, sortable, searchable, value(row), csv(row), order }:
// `value` is what the column sorts by, `csv` is the text the cell shows.

// Class for a column and its cells that a phone drops, keeping the row
// readable; the status column and the row's identity never use it.
export const SECONDARY_COLUMN = 'd-none d-md-table-cell';
// For the positions table, which becomes cards below the lg breakpoint
export const SECONDARY_COLUMN_LG = 'd-none d-lg-table-cell';
// For a column a wide table can only afford from the xl breakpoint
export const SECONDARY_COLUMN_XL = 'd-none d-xl-table-cell';
// Below md a request table is drawn as cards (RequestCardList)
export const PHONE_QUERY = '(max-width: 767.98px)';

export const isEmpty = (value) =>
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value));

const TEXT_COLLATOR = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base',
});

function compareNonEmpty(type, a, b, order) {
    if (type === 'number') {
        const diff = Number(a) - Number(b);
        // A value that is not a number (a CRN with a letter) sorts as text
        return Number.isNaN(diff)
            ? TEXT_COLLATOR.compare(String(a), String(b))
            : diff;
    }
    if (type === 'date') return new Date(a).getTime() - new Date(b).getTime();
    if (type === 'status') {
        const rank = (value) => {
            const at = (order || []).indexOf(value);
            return at === -1 ? (order || []).length : at;
        };
        const diff = rank(a) - rank(b);
        return diff !== 0 ? diff : TEXT_COLLATOR.compare(String(a), String(b));
    }
    return TEXT_COLLATOR.compare(String(a), String(b));
}

/**
 * A sorted copy of `rows`. Empty values go last in both directions, and rows
 * that compare equal keep their incoming order.
 */
export function sortRows(rows, column, direction) {
    if (!column) return rows;
    const sign = direction === 'desc' ? -1 : 1;
    return rows
        .map((row, index) => ({ row, index, value: column.value(row) }))
        .sort((a, b) => {
            const aEmpty = isEmpty(a.value);
            const bEmpty = isEmpty(b.value);
            if (aEmpty || bEmpty) {
                if (aEmpty && bEmpty) return a.index - b.index;
                return aEmpty ? 1 : -1;
            }
            const diff = compareNonEmpty(
                column.type,
                a.value,
                b.value,
                column.order
            );
            return diff !== 0 ? sign * diff : a.index - b.index;
        })
        .map((entry) => entry.row);
}

export const cellText = (column, row) => {
    const text = column.csv ? column.csv(row) : column.value(row);
    return isEmpty(text) ? '' : String(text);
};

export const isSearchable = (column) =>
    column.searchable !== undefined
        ? column.searchable
        : !column.type || column.type === 'text';

/** Rows whose searchable columns contain the trimmed, case-folded query. */
export function searchRows(rows, columns, query) {
    const needle = (query || '').trim().toLowerCase();
    if (needle === '') return rows;
    const searchable = columns.filter(isSearchable);
    return rows.filter((row) =>
        searchable.some((column) =>
            cellText(column, row).toLowerCase().includes(needle)
        )
    );
}

const FORMULA_LEADS = new Set(['=', '+', '-', '@', '\t', '\r']);

/** One CSV field: formula-leading text is defanged, then RFC 4180 quoting. */
export function csvField(text) {
    let value = text === null || text === undefined ? '' : String(text);
    if (value !== '' && FORMULA_LEADS.has(value[0])) value = `'${value}`;
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The CSV for exactly these columns and rows, header row first. */
export function buildCsv(columns, rows) {
    const lines = [
        columns.map((column) => csvField(column.label)).join(','),
        ...rows.map((row) =>
            columns.map((column) => csvField(cellText(column, row))).join(',')
        ),
    ];
    return `${lines.join('\r\n')}\r\n`;
}

export const personText = (name, email) =>
    [name, email && `<${email}>`].filter(Boolean).join(' ');
